import { darken } from 'polished';
import { css } from 'styled-components';

import { theme } from '../../theme';
import { ButtonSizeEnum, ButtonVariantEnum, IButton, IconPositionEnum } from './button.types';

const buttonStylesPerSize = {
	[ButtonSizeEnum.SMALL]: css`
		height: 32px;
		min-width: 88px;
		padding: 0 16px;
	`,
	[ButtonSizeEnum.MEDIUM]: css`
		height: 40px;
		min-width: 96px;
		padding: 0 16px;
	`,
	[ButtonSizeEnum.LARGE]: css`
		height: 48px;
		min-width: 120px;
		padding: 0 24px;
	`,
};

export const getIconSize = (variant: ButtonSizeEnum) => {
	switch (variant) {
		case ButtonSizeEnum.LARGE:
			return 24;
		default:
			return 20;
	}
};

const getButtonColor = (variant: ButtonVariantEnum) => {
	switch (variant) {
		case ButtonVariantEnum.SECONDARY_DARK:
			return theme.colors.text.dark;
		case ButtonVariantEnum.DISABLED:
			return theme.colors.text.disabled;
		default:
			return theme.colors.text.white;
	}
};

const getButtonBorderColor = (variant: ButtonVariantEnum, active: boolean) => {
	switch (variant || active) {
		case ButtonVariantEnum.SECONDARY_DARK:
			return active
				? `1px solid ${theme.colors.button.border.secondaryDarkHover}`
				: `1px solid ${theme.colors.button.border.secondaryDark}`;
		case ButtonVariantEnum.SECONDARY_LIGHT:
			return active
				? `1px solid ${theme.colors.button.border.secondaryLightHover}`
				: `1px solid ${theme.colors.button.border.secondaryLight}`;
		default:
			return 'none';
	}
};

const getButtonBackgroundColorOnHover = (variant: ButtonVariantEnum) => {
	if (variant === ButtonVariantEnum.DISABLED) {
		return '';
	} else {
		return darken('0.05', theme.colors.button.background[variant]);
	}
};

const calculatePaddingForButtonWithIcon = (buttonSize: ButtonSizeEnum, iconPosition: IconPositionEnum) => {
	const paddingSize = buttonSize === ButtonSizeEnum.LARGE ? '16px' : '8px';
	const paddingSide = iconPosition === IconPositionEnum.LEFT ? 'padding-left' : 'padding-right';
	return css`
		${paddingSide}: ${paddingSize};
	`;
};

export const baseButtonStyles = css<IButton>`
	margin: 0;
	outline: none;
	border-radius: 4px;
	font-size: 15px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: ${(props) => getButtonBorderColor(props.variant || ButtonVariantEnum.PRIMARY, props.active || false)};
	color: ${(props) => getButtonColor(props.variant || ButtonVariantEnum.PRIMARY)};
	font-family: ${theme.typography.fonts.semibold};
	background-color: ${(props) =>
		props.variant
			? theme.colors.button.background[props.variant]
			: theme.colors.button.background[ButtonVariantEnum.PRIMARY]};
	cursor: ${(props) => (props.disabled ? `default` : `pointer`)};
	${(props) => buttonStylesPerSize[props.size || ButtonSizeEnum.MEDIUM]};
	${(props) =>
		props.icon &&
		calculatePaddingForButtonWithIcon(
			props.size || ButtonSizeEnum.MEDIUM,
			props.iconPosition || IconPositionEnum.RIGHT
		)};
	&:hover {
		transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
		background-color: ${(props) => getButtonBackgroundColorOnHover(props.variant || ButtonVariantEnum.PRIMARY)};
		border-color: ${(props) => {
			if (props.variant === ButtonVariantEnum.SECONDARY_DARK) {
				return theme.colors.button.border.secondaryDarkHover;
			}
			if (props.variant === ButtonVariantEnum.SECONDARY_LIGHT) {
				return theme.colors.button.border.secondaryLightHover;
			} else {
				return '';
			}
		}};
	}
	svg {
		margin: ${(props) =>
			props.iconPosition && props.iconPosition === IconPositionEnum.LEFT ? `0 8px 0 0` : `0 0 0 8px`};
	}
`;
